import { getToken } from '../../Utils/authUtils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { HL7 } from '../../constant';

export interface DistributionRecord {
  _id: string;
  reportId: string;
  studyInstanceUID?: string;
  type: string;
  fromProviderId?: string;
  fromProviderName?: string;
  toProviderId?: string;
  toProviderName?: string;
  /** Name in parts, on records saved since they were stored. See Distribution model. */
  toProviderFirstName?: string;
  toProviderLastName?: string;
  toIdType?: string;
  /** Email channel only — the address the report PDF was sent to. */
  toEmail?: string;
  /** Email channel only — SES MessageId, for tracing a delivery with AWS. */
  emailMessageId?: string;
  /** Email channel only — the covering note the sender added. */
  emailNote?: string;
  format?: string;
  status: string;
  httpStatus?: number;
  ackCode?: string;
  ackErrorMessage?: string;
  msaMessageControlId?: string;
  requestMeta?: { siteId?: string | number; hostname?: string };
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DistributionHistoryPagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface DistributionHistoryResult {
  data: DistributionRecord[];
  pagination: DistributionHistoryPagination;
}

export interface GetDistributionHistoryPayload {
  reportId: string;
  page?: number;
  limit?: number;
}

export interface GetDistributionHistoryByStudyPayload {
  studyInstanceUID: string;
  page?: number;
  limit?: number;
}

export const getDistributionHistoryApi = createAsyncThunk(
  'hl7/getDistributionHistory',
  async (payload: GetDistributionHistoryPayload, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) {
        return rejectWithValue('No token found');
      }
      const { reportId, page = 1, limit = 20 } = payload;
      const url = `${HL7.HISTORY(reportId)}?page=${page}&limit=${limit}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return rejectWithValue(result.msg || `History fetch failed: ${response.status}`);
      }
      return result.result as DistributionHistoryResult;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDistributionHistoryByStudyApi = createAsyncThunk(
  'hl7/getDistributionHistoryByStudy',
  async (payload: GetDistributionHistoryByStudyPayload, { rejectWithValue }) => {
    try {
      const token = getToken();
      if (!token) {
        return rejectWithValue('No token found');
      }
      const { studyInstanceUID, page = 1, limit = 50 } = payload;
      const url = `${HL7.HISTORY_BY_STUDY(studyInstanceUID)}?page=${page}&limit=${limit}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (!response.ok) {
        return rejectWithValue(result.msg || `History by study failed: ${response.status}`);
      }
      return result.result as DistributionHistoryResult;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
